import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { Close, OpenInNew, TableChart } from "@material-ui/icons";
import StreamflowChartContainer from "../../../components/charts/streamflowChart/StreamflowChartContainer";
import RawTable from "../../../components/RawTable";
import { getWaterYearRange } from "../../fieldDepthProfile/FieldDepthProfile";
import { ROUTES } from "../../../constants";
import useFetchGraphData from "../../../hooks/useFetchGraphData";

const PARAMETER_OPTIONS = [
  { label: "Flow", value: "2" },
  { label: "Stage", value: "1901" },
];

const COLUMNS = [
  { title: "Location", field: "location_name" },
  { title: "Parameter", field: "parameter" },
  { title: "Report Date", field: "rdate", type: "date" },
  { title: "Daily Average", field: "daily_average", type: "numeric" },
  { title: "Daily Min", field: "daily_min", type: "numeric" },
  { title: "Daily Max", field: "daily_max", type: "numeric" },
  { title: "Units", field: "result_unit_name" },
];

const { start_date: defaultStartDate, end_date: defaultEndDate } =
  getWaterYearRange();

export default function StreamflowDataDialog() {
  const [open, setOpen] = useState(false);
  const [initialLocationId, setInitialLocationId] = useState(null);
  const [selectedLocationIds, setSelectedLocationIds] = useState([]);
  const [parameterId, setParameterId] = useState("2");
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [showTable, setShowTable] = useState(false);

  const { data: locationsData } = useFetchGraphData(
    {},
    "streamflow-data/locations"
  );

  const locationOptions = useMemo(() => {
    if (!locationsData?.length) return [];
    return locationsData.map((loc) => ({
      label: loc.location_name,
      value: String(loc.locationid),
    }));
  }, [locationsData]);

  const readUrlParams = useCallback(() => {
    const sp = new URLSearchParams(window.location.search);
    const loc = sp.get("streamflow_loc");
    const param = sp.get("streamflow_param");
    if (loc) {
      setInitialLocationId(loc);
      setSelectedLocationIds([loc]);
      if (param) setParameterId(param);
      setStartDate(defaultStartDate);
      setEndDate(defaultEndDate);
      setShowTable(false);
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    readUrlParams();
    window.addEventListener("popstate", readUrlParams);
    return () => window.removeEventListener("popstate", readUrlParams);
  }, [readUrlParams]);

  const handleClose = () => {
    setOpen(false);
    const url = new URL(window.location);
    url.searchParams.delete("streamflow_loc");
    url.searchParams.delete("streamflow_param");
    window.history.pushState({}, "", url);
    // keep react-router's location in sync with the manual history change
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  const locationIdString = useMemo(
    () => selectedLocationIds.join(","),
    [selectedLocationIds]
  );

  const tableParams = useMemo(
    () =>
      locationIdString
        ? {
            parameterid: Number(parameterId),
            locationid: locationIdString,
            start_date: startDate,
            end_date: endDate,
          }
        : null,
    [parameterId, locationIdString, startDate, endDate]
  );

  const handleOpenFullPage = () => {
    const params = new URLSearchParams({
      parameterid: parameterId,
      locationid: locationIdString,
      start_date: startDate,
      end_date: endDate,
    });
    window.open(`${ROUTES.STREAMFLOW_DATA}?${params.toString()}`, "_blank");
  };

  const selectedOptions = useMemo(
    () => locationOptions.filter((o) => selectedLocationIds.includes(o.value)),
    [locationOptions, selectedLocationIds]
  );

  if (!initialLocationId) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Streamflow Data
        <IconButton
          aria-label="close"
          onClick={handleClose}
          style={{ position: "absolute", right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box
          display="flex"
          flexWrap="wrap"
          style={{ gap: 16 }}
          mb={2}
          alignItems="center"
        >
          <FormControl
            variant="outlined"
            size="small"
            style={{ minWidth: 150 }}
          >
            <InputLabel>Parameter</InputLabel>
            <Select
              value={parameterId}
              onChange={(e) => setParameterId(e.target.value)}
              label="Parameter"
            >
              {PARAMETER_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Start Date"
            type="date"
            size="small"
            variant="outlined"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="End Date"
            type="date"
            size="small"
            variant="outlined"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <Autocomplete
            multiple
            size="small"
            disableCloseOnSelect
            options={locationOptions}
            value={selectedOptions}
            onChange={(_, picks) =>
              setSelectedLocationIds(picks.map((p) => p.value))
            }
            getOptionLabel={(o) => o.label}
            getOptionSelected={(a, b) => a.value === b.value}
            style={{ minWidth: 300, flexGrow: 1 }}
            renderInput={(p) => (
              <TextField
                {...p}
                label="Locations"
                variant="outlined"
                size="small"
              />
            )}
          />
        </Box>

        <StreamflowChartContainer
          parameterid={Number(parameterId)}
          locationid={locationIdString}
          startDate={startDate}
          endDate={endDate}
          height={400}
        />

        <Box mt={2}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={showTable ? 2 : 0}
          >
            <strong>Raw Data Table</strong>
            <Button
              variant="outlined"
              size="small"
              startIcon={<TableChart />}
              onClick={() => setShowTable((prev) => !prev)}
            >
              {showTable ? "Hide Table" : "Show Table"}
            </Button>
          </Box>
          {showTable && tableParams && (
            <RawTable
              endPoint="streamflow-data"
              params={tableParams}
              columns={COLUMNS}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleOpenFullPage}
          color="primary"
          startIcon={<OpenInNew />}
        >
          Open Full Page
        </Button>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
